import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from '@/deploy/components/layout/PageHeader';
import { Button } from '@/deploy/components/ui/Button';
import { Badge } from '@/deploy/components/ui/Badge';
import { Icon } from '@/deploy/components/ui/Icon';
import { Modal } from '@/deploy/components/ui/Modal';
import { PermissionLock } from '@/deploy/components/ui/PermissionLock';
import { JobModal } from '@/deploy/components/network/JobModal';
import { VhostFormModal } from '@/deploy/components/network/VhostFormModal';
import { describeRule } from '@/deploy/components/network/AccessRulesEditor';
import { errorMessage, expiryBadge } from '@/deploy/components/network/networkFormat';
import styles from '@/deploy/components/network/Network.module.scss';
import { networkService } from '@/deploy/services/network.service';
import { useSessionStore } from '@/deploy/stores/session.store';
import { can } from '@/deploy/utils/permissions';
import type { Certificate, NetworkJob, ProjectDomains, Vhost } from '@/deploy/types/network';

type Dialog =
  | { kind: 'create' }
  | { kind: 'edit' | 'adopt'; vhost: Vhost }
  | { kind: 'config'; vhost: Vhost }
  | { kind: 'delete'; vhost: Vhost }
  | { kind: 'job'; job: NetworkJob; title: string };

/** Réseau > Domaines : vhosts du reverse proxy du projet actif. */
export function NetworkDomains() {
  const project = useSessionStore((s) => s.currentProject());
  const user = useSessionStore((s) => s.user);
  const canEdit = can(user, 'cicd.network');

  const [vhosts, setVhosts] = useState<Vhost[]>([]);
  const [domains, setDomains] = useState<ProjectDomains | null>(null);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState<Dialog | null>(null);

  const load = useCallback(async () => {
    if (!project.id) return;
    setError(null);
    try {
      const [v, d, c] = await Promise.all([
        networkService.listVhosts(project.id),
        networkService.domains(project.id),
        networkService.listCertificates(project.id, 'INTERNAL'),
      ]);
      setVhosts(v);
      setDomains(d);
      setCertificates(c);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? vhosts.filter((v) => v.domain.includes(q) || v.upstreams.some((u) => u.includes(q))) : vhosts;
  }, [vhosts, search]);

  const openJob = (job: NetworkJob, title: string) => setDialog({ kind: 'job', job, title });
  const fetchJob = useCallback((id: number) => networkService.job(project.id, id), [project.id]);
  const noDomain = domains !== null && domains.domains.length === 0;

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Réseau"
        title="Domaines"
        subtitle={`Sous-domaines servis par le reverse proxy pour ${project.name} : DNS, certificat et configuration nginx.`}
        actions={
          <Button
            variant="primary"
            leftIcon={<Icon name="plus" size={14} />}
            onClick={() => setDialog({ kind: 'create' })}
            disabled={!canEdit || !domains || noDomain}
            title={!canEdit ? 'Permission cicd.network requise' : undefined}
          >
            Nouveau domaine
            {!canEdit && <PermissionLock inline />}
          </Button>
        }
      />

      {error && <div className={styles.error}>{error}</div>}
      {noDomain && (
        <div className={styles.info}>
          Aucun domaine principal n'est attribué à ce projet. Un admin les règle dans <code>Paramètres &gt; Réseau</code>.
        </div>
      )}

      <div className={styles.toolbar}>
        <input className={styles.search} placeholder="Rechercher un domaine ou un upstream" value={search}
          onChange={(e) => setSearch(e.target.value)} />
        <span className={styles.spacer} />
        {domains && domains.domains.length > 0 && (
          <span className={styles.muted}>{domains.domains.map((d) => d.name).join(' · ')}</span>
        )}
        <Button variant="ghost" size="sm" leftIcon={<Icon name="refresh" size={12} />} onClick={() => void load()}>
          Rafraîchir
        </Button>
      </div>

      {loading ? (
        <div className={styles.empty}>Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className={styles.empty}>{search ? 'Aucun domaine ne correspond.' : 'Aucun domaine pour ce projet.'}</div>
      ) : (
        <div className={styles.list}>
          <div className={styles.rowHead}>
            <span>Domaine</span>
            <span>Upstream</span>
            <span>Certificat</span>
            <span />
          </div>
          {filtered.map((v) => {
            const expiry = expiryBadge(v.certificateExpiresAt);
            return (
              <div key={v.id} className={styles.row}>
                <div className={styles.cell}>
                  <a className={styles.primary} href={`https://${v.domain}`} target="_blank" rel="noreferrer">{v.domain}</a>
                  <div className={styles.cellInline}>
                    {v.mode === 'RAW'
                      ? <Badge tone="warning">Importé</Badge>
                      : <Badge tone="muted">Géré</Badge>}
                    {v.websocket && <Badge tone="info">WebSocket</Badge>}
                    {v.accessRules.length > 0 && (
                      <span title={v.accessRules.map(describeRule).join('\n')}>
                        <Badge tone="neutral">{protectionLabel(v)}</Badge>
                      </span>
                    )}
                    {v.htpasswdFile && <Badge tone="neutral">htpasswd</Badge>}
                    {v.dnsManaged && <Badge tone="muted">DNS</Badge>}
                  </div>
                </div>
                <div className={styles.cell}>
                  {v.upstreams.length === 0
                    ? <span className={styles.muted}>-</span>
                    : v.upstreams.map((u) => <span key={u} className={styles.mono}>{u}</span>)}
                </div>
                <div className={styles.cellInline}>
                  <Badge tone={expiry.tone} dot>{expiry.label}</Badge>
                  {v.certificateName && v.certificateName !== v.domain && <span className={styles.muted}>{v.certificateName}</span>}
                </div>
                <div className={styles.actions}>
                  <Button variant="ghost" size="sm" leftIcon={<Icon name="eye" size={12} />} onClick={() => setDialog({ kind: 'config', vhost: v })}>
                    Conf
                  </Button>
                  {v.mode === 'MANAGED' ? (
                    <Button variant="ghost" size="sm" leftIcon={<Icon name="pencil" size={12} />} disabled={!canEdit}
                      onClick={() => setDialog({ kind: 'edit', vhost: v })}>
                      Modifier
                    </Button>
                  ) : (
                    <Button variant="softWarning" size="sm" leftIcon={<Icon name="refresh" size={12} />} disabled={!canEdit}
                      onClick={() => setDialog({ kind: 'adopt', vhost: v })}>
                      Reprendre
                    </Button>
                  )}
                  <Button variant="softDanger" size="sm" leftIcon={<Icon name="x" size={12} />} disabled={!canEdit}
                    onClick={() => setDialog({ kind: 'delete', vhost: v })}>
                    Supprimer
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {dialog?.kind === 'create' && domains && (
        <VhostFormModal projectId={project.id} mode="create" domains={domains} certificates={certificates}
          onClose={() => setDialog(null)} onJob={openJob} />
      )}
      {(dialog?.kind === 'edit' || dialog?.kind === 'adopt') && domains && (
        <VhostFormModal projectId={project.id} mode={dialog.kind} domains={domains} certificates={certificates}
          vhost={dialog.vhost} onClose={() => setDialog(null)} onJob={openJob} />
      )}
      {dialog?.kind === 'config' && (
        <ConfigModal projectId={project.id} vhost={dialog.vhost} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'delete' && (
        <DeleteVhostModal projectId={project.id} vhost={dialog.vhost} onClose={() => setDialog(null)} onJob={openJob} />
      )}
      {dialog?.kind === 'job' && (
        <JobModal job={dialog.job} title={dialog.title} fetchJob={fetchJob}
          onClose={() => setDialog(null)} onDone={() => void load()} />
      )}
    </div>
  );
}

/** Protections présentes sur le vhost, toutes règles confondues : « IP · Basic · Bearer ». */
function protectionLabel(v: Vhost): string {
  const kinds = [
    v.accessRules.some((r) => r.allowIps.length > 0) && 'IP',
    v.accessRules.some((r) => r.basicUsers.length > 0) && 'Basic',
    v.accessRules.some((r) => r.bearerKeys.length > 0) && 'Bearer',
  ].filter(Boolean);
  return kinds.join(' · ');
}

function ConfigModal({ projectId, vhost, onClose }: { projectId: string; vhost: Vhost; onClose: () => void }) {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    networkService.vhostConfig(projectId, vhost.domain).then(setContent).catch((e) => setError(errorMessage(e)));
  }, [projectId, vhost.domain]);

  return (
    <Modal open onClose={onClose} title={vhost.domain} subtitle={vhost.configFile} width="lg"
      footer={<Button variant="primary" onClick={onClose}>Fermer</Button>}>
      {error ? <div className={styles.error}>{error}</div> : <pre className={styles.code}>{content ?? 'Chargement…'}</pre>}
    </Modal>
  );
}

function DeleteVhostModal({ projectId, vhost, onClose, onJob }: {
  projectId: string;
  vhost: Vhost;
  onClose: () => void;
  onJob: (job: NetworkJob, title: string) => void;
}) {
  const [deleteDns, setDeleteDns] = useState(true);
  const [deleteCertificate, setDeleteCertificate] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      onJob(await networkService.deleteVhost(projectId, vhost.domain, deleteDns, deleteCertificate), `Suppression : ${vhost.domain}`);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Supprimer le domaine" subtitle={vhost.domain} width="sm"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
        <Button variant="danger" onClick={submit} loading={busy}>Supprimer</Button>
      </>}>
      <div className={styles.form}>
        <span>Le fichier nginx est retiré puis tous les nodes sont rechargés.</span>
        {vhost.dnsManaged && (
          <label className={styles.checkbox}>
            <input type="checkbox" checked={deleteDns} onChange={(e) => setDeleteDns(e.target.checked)} />
            Supprimer aussi le CNAME Cloudflare
          </label>
        )}
        {vhost.certificateId && (
          <label className={styles.checkbox}>
            <input type="checkbox" checked={deleteCertificate} onChange={(e) => setDeleteCertificate(e.target.checked)} />
            Supprimer le certificat s'il ne sert plus à aucun autre domaine
          </label>
        )}
        {error && <div className={styles.error}>{error}</div>}
      </div>
    </Modal>
  );
}
